import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/utils/cn'

/**
 * 面板与卡片外壳。
 *
 * 设计稿里所有内容都装在「白底 + 1px 蓝灰描边 + 大圆角」的容器里（深色主题下
 * 换成提亮的 navy 底）。抽成组件是为了让圆角/描边/阴影只有一处定义 ——
 * 否则同一个视觉元素会在十个文件里出现十种半径。
 */

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  /** 去掉内边距，供需要自己控制留白的场景（比如内部有图片要出血）。 */
  flush?: boolean
}

export function Panel({ flush = false, className, children, ...props }: PanelProps) {
  return (
    <div
      className={cn(
        'rounded-panel border border-border bg-surface shadow-panel',
        !flush && 'p-4 sm:p-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface SectionHeaderProps {
  icon?: ReactNode
  title: string
  /** 右上角的「查看全部 ›」之类。 */
  action?: ReactNode
  className?: string
  /** 标题层级。首页各区块是 h2，页面主标题在别处用 h1。 */
  as?: 'h1' | 'h2' | 'h3'
}

/** 区块标题（设计稿「下载最多」「最新更新」那一行）。 */
export function SectionHeader({
  icon,
  title,
  action,
  className,
  as: Heading = 'h2',
}: SectionHeaderProps) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <Heading className="flex items-center gap-2 text-base font-semibold tracking-tight text-foreground sm:text-lg">
        {icon && <span className="text-primary">{icon}</span>}
        {title}
      </Heading>
      {action}
    </div>
  )
}

/**
 * 图片占位。
 *
 * 设计稿里所有缩略图都是「蓝灰色块 + 居中圆角小方块」——那不是随手画的灰框，
 * 而是**没有封面图时的正式外观**：无封面、封面已删除或字节已清理时
 * `cover_public_key` 整个缺省，这个组件就是那种情况下的展示态。
 */
export function ImagePlaceholder({
  className,
  iconClassName,
}: {
  className?: string
  iconClassName?: string
}) {
  return (
    <div
      className={cn('flex items-center justify-center bg-placeholder', className)}
      // 装饰性：外层容器已经有可访问的标题文字，这里不该再被读一遍。
      aria-hidden="true"
    >
      <div className={cn('size-10 rounded-lg bg-background/60', iconClassName)} />
    </div>
  )
}

/** 广告位（设计稿侧栏与详情页右栏的虚线框）。 */
export function AdSlot({ className }: { className?: string }) {
  void className
  return null
}
